/**
 * A stored reference that reads a list by position (Feature 82 D6): the
 * agent refuses it on save (D4), so the editor keeps it where it stands,
 * marked, and Save waits, each problem located as the agent locates it: a
 * link's gate, a decision, or a step's mapping. A template saved before
 * Feature 82 still runs; only its next save needs the path rewritten.
 */
import type {
  LinkageListTest,
  LinkageProblem,
  LinkageRule,
  LinkageSource,
} from '@optum/api-client/types';

import type { ComboboxOption } from '../../ui/Combobox.js';
import { linkId, type Draft } from './draft.js';
import { isListFilter, isListTest } from './lists.js';
import { shortRef } from './references.js';

// A segment that is a number: an item by its place in a list.
const POSITION = /^\d+$/;
/** The problem's words, after the path: what the agent says. */
export const POSITION_WORDS = 'reads a list by position: use a list test or a list filter';
/** Said under a field that holds such a path. */
export const POSITION_HINT =
  'Reads a list by position: the agent no longer takes it. Remove it and test the list with “any item where”.';

/** Whether a stored path reads a list's item by its place. */
export function hasPosition(path: string): boolean {
  return path.split('.').some((segment) => POSITION.test(segment));
}

/**
 * The option a picker shows for a stored path it does not offer: one with a
 * position in short form, marked as stored (D6); any other with `why`.
 */
export function storedOption(path: string, why: string): ComboboxOption {
  return hasPosition(path)
    ? { value: path, label: shortRef(path), aside: 'as stored', asideTone: 'warn' }
    : { value: path, label: `${path} · ${why}` };
}

/** Every path a gate's or a branch's rules read: each rule's, and each list test's items'. */
function rulePaths(items: readonly (LinkageRule | LinkageListTest)[]): string[] {
  return items.flatMap((rule) =>
    isListTest(rule) ? [rule.ref, ...rule.where.items.map((r) => r.ref)] : [rule.ref],
  );
}

/** Every path a mapping source reads: a reference, or a filter's list, item rules, and pick. */
function sourcePaths(source: LinkageSource): string[] {
  if (!('ref' in source)) return [];
  if (!isListFilter(source)) return [source.ref];
  return [
    source.ref,
    ...(source.where?.items ?? []).map((r) => r.ref),
    ...(source.pick !== undefined ? [source.pick] : []),
  ];
}

/** One problem on a path with a position, at its node. */
function problem(where: string, path: string): LinkageProblem {
  return { where, code: 'index_path', message: `${shortRef(path)} ${POSITION_WORDS}` };
}

/** What Save waits on: each stored path that reads a list by position (D6). */
export function positionProblems(draft: Draft): LinkageProblem[] {
  const located: [string, string][] = [
    ...draft.links.flatMap((link) =>
      rulePaths(link.rules.items).map((path): [string, string] => [
        linkId(link.from, link.to),
        path,
      ]),
    ),
    ...draft.decisions.flatMap((decision) =>
      [
        ...(decision.ref ? [decision.ref] : []),
        ...decision.branches.flatMap((branch) => rulePaths(branch.rules?.items ?? [])),
      ].map((path): [string, string] => [decision.name, path]),
    ),
    ...draft.steps.flatMap((step) =>
      Object.values(step.mapping)
        .flatMap(sourcePaths)
        .map((path): [string, string] => [step.name, path]),
    ),
  ];
  return located
    .filter(([, path]) => hasPosition(path))
    .map(([where, path]) => problem(where, path));
}
